import { html } from 'htm/preact';
import { useState, useEffect, useContext, useRef } from 'preact/hooks';
import {
  FormContext,
  Label,
  Description,
  Errors,
  Textfield,
} from '../../../bpmn.io/@bpmn-io/form-js';
import classNames from '../../../bpmn.io/classnames';

export const type = 'expandableTable';

// ─── utilidades ─────────────────────────────────────────────────────────────

function editorFormFieldClasses(fieldType, { disabled = false, readonly = false } = {}) {
  if (!fieldType) throw new Error('type required');
  return classNames('fjs-form-field', `fjs-form-field-${fieldType}`, {
    'fjs-disabled': disabled,
    'fjs-readonly': readonly,
  });
}

function prefixId(id, formId) {
  return formId ? `fjs-form-${formId}-${id}` : `fjs-form-${id}`;
}

/**
 * Lee las columnas directamente desde field.columns (igual que el componente Tabla nativo).
 * Cada columna: { key, label, type: 'text'|'number'|'date'|'textarea'|'checkbox'|'select', visibleInTable: bool }
 * Las columnas 'select' pueden incluir: values: [{label, value, valorCascada}], cascada: bool, llaveCascada: string
 */
function parseColumns(field) {
  return Array.isArray(field.columns) ? field.columns : [];
}

/**
 * Resuelve la etiqueta a mostrar en la tabla para una celda de tipo 'select',
 * buscando la opción cuyo value coincida con el valor almacenado en la fila.
 */
function getSelectOptionLabel(col, val) {
  if (val === undefined || val === null || val === '') return '';
  const values = Array.isArray(col.values) ? col.values : [];
  const found = values.find(v => String(v.value) === String(val));
  return found ? found.label : val;
}

/**
 * Normaliza el valor del campo a un array de filas.
 * Acepta:
 *   - Array de objetos              → lo devuelve tal cual
 *   - JSON string de array          → '[{...}]' → parsea
 *   - JSON string de objeto único   → '{...}'   → envuelve en [obj]
 *   - Dos o más objetos sin corchetes: '{...}, {...}' → envuelve en [...]
 *   - Objeto plano                  → [obj]
 *   - Cualquier otra cosa           → []
 */
function parseRowValue(v) {
  if (Array.isArray(v)) return v;

  // Objeto con formato indexado {"#arr":true,"0":{...},"1":{...}} (generado por noArrayForBackend)
  if (v && typeof v === 'object' && !Array.isArray(v)) {
    if (v['#arr'] === true) {
      return Object.keys(v)
        .filter(k => k !== '#arr')
        .sort((a, b) => Number(a) - Number(b))
        .map(k => v[k]);
    }
    return [v];
  }

  if (typeof v === 'string' && v.trim()) {
    const s = v.trim();
    // Intento 1: JSON estándar
    try {
      const parsed = JSON.parse(s);
      if (Array.isArray(parsed)) return parsed;
      if (parsed && typeof parsed === 'object') {
        // Formato indexado persistido como string
        if (parsed['#arr'] === true) {
          return Object.keys(parsed)
            .filter(k => k !== '#arr')
            .sort((a, b) => Number(a) - Number(b))
            .map(k => parsed[k]);
        }
        return [parsed];
      }
    } catch (_) {}
    // Intento 2: múltiples objetos sin corchetes → '{...}, {...}'
    try {
      const parsed = JSON.parse('[' + s + ']');
      if (Array.isArray(parsed)) return parsed;
    } catch (_) {}
  }
  return [];
}

// ─── campo individual dentro del modal ──────────────────────────────────────

/**
 * Filtra las opciones de una columna 'select' según su configuración de cascada.
 * Misma lógica que CascadeSelect (extension/cascade-select), pero aplicada
 * dentro del contexto de una fila de la tabla expandible: el "padre" es otra
 * columna de la MISMA fila (col.llaveCascada apunta al key de esa columna).
 */
function filterSelectValues(col, parentValue) {
  const values = Array.isArray(col.values) ? col.values : [];
  if (!col.cascada || !col.llaveCascada) return values;
  if (parentValue === null || parentValue === undefined || parentValue === '') return [];
  return values.filter(
    (opt) =>
      opt.valorCascada !== undefined &&
      opt.valorCascada !== null &&
      String(opt.valorCascada) === String(parentValue)
  );
}

// Las columnas tipo 'checkbox' son selectores de fila: NO aparecen en el modal
function ModalField({ col, value, onChange, disabled, parentValue }) {
  const { key, label, type: colType = 'text', values = [], cascada = false, llaveCascada = '' } = col;
  const id = `modal-field-${key}`;

  const style = 'width:100%; padding:6px 8px; border:1px solid #ccc; border-radius:3px; box-sizing:border-box; font-size:13px;';

  // Cascada: limpiar el valor de este campo cuando cambia el valor del campo padre
  const prevParentValueRef = useRef(undefined);
  useEffect(() => {
    if (colType !== 'select' || !cascada || !llaveCascada) return;
    const isFirstRun = prevParentValueRef.current === undefined;
    const oldValue = prevParentValueRef.current;
    prevParentValueRef.current = parentValue;

    if (!isFirstRun && String(oldValue) !== String(parentValue)) {
      onChange(key, '');
    }
  }, [parentValue, cascada, llaveCascada, colType]);

  if (colType === 'select') {
    const filteredValues = filterSelectValues(col, parentValue);
    return html`
      <div style="margin-bottom:12px;">
        <label for=${id} style="display:block;margin-bottom:4px;font-weight:600;font-size:13px;">${label}</label>
        <select id=${id} style=${style} disabled=${disabled}
          onChange=${e => onChange(key, e.target.value)} value=${value ?? ''}>
          <option value="">-- Seleccione --</option>
          ${filteredValues.map(opt => html`<option value=${opt.value}>${opt.label}</option>`)}
        </select>
      </div>`;
  }

  if (colType === 'textarea') {
    return html`
      <div style="margin-bottom:12px;">
        <label for=${id} style="display:block;margin-bottom:4px;font-weight:600;font-size:13px;">${label}</label>
        <textarea id=${id} rows="3" style=${style} disabled=${disabled}
          onInput=${e => onChange(key, e.target.value)} value=${value || ''}></textarea>
      </div>`;
  }

  const inputType = colType === 'number' ? 'number' : colType === 'date' ? 'date' : 'text';

  return html`
    <div style="margin-bottom:12px;">
      <label for=${id} style="display:block;margin-bottom:4px;font-weight:600;font-size:13px;">${label}</label>
      <input id=${id} type=${inputType} style=${style} disabled=${disabled}
        onInput=${e => onChange(key, e.target.value)} value=${value || ''} />
    </div>`;
}

// ─── modal de agregar / editar / ver ────────────────────────────────────────
//  isReadOnly=true  → vista interna, todos los campos deshabilitados, sin guardar
//  isEdit=true      → edición, botón 'Guardar cambios'
//  ambos false      → alta, botón 'Agregar'

function RowModal({ columns, rowData, onSave, onClose, isEdit, isReadOnly }) {
  const [data, setData] = useState({ ...(rowData || {}) });

  const handleChange = (key, val) => setData(prev => ({ ...prev, [key]: val }));

  // Columnas que van al modal: excluye las de tipo checkbox (son selectores de fila)
  const formCols = columns.filter(c => c.type !== 'checkbox');

  const handleSave = () => { if (!isReadOnly) onSave(data); };

  const title = isReadOnly ? 'Ver registro' : isEdit ? 'Editar registro' : 'Agregar registro';

  return html`
    <div
      class="modal-diagrama-viewer"
      style="
        position:fixed; top:0; left:0; width:100%; height:100%;
        background:rgba(0,0,0,0.45); z-index:9999;
        display:flex; align-items:center; justify-content:center;
      "
      onClick=${(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        class="ms-Dialog ms-Dialog--lgHeader diagrama-viewer"
        style="
          width:540px; max-width:95vw;
          height:auto; max-height:85vh;
          min-height:auto;
          display:flex; flex-direction:column;
          overflow:hidden;
        "
        onClick=${(e) => e.stopPropagation()}
      >
        <div class="ms-Dialog-title" style="display:flex; align-items:center; justify-content:space-between;">
          <span>${title}</span>
        </div>

        <div class="ms-Dialog-content" style="overflow-y:auto; padding:16px 20px;">
          ${formCols.length === 0
            ? html`<p style="color:#888;font-style:italic;margin:0;">No hay campos editables configurados.</p>`
            : formCols.map(col => html`
              <${ModalField}
                col=${col}
                value=${data[col.key]}
                parentValue=${col.type === 'select' && col.llaveCascada ? data[col.llaveCascada] : undefined}
                onChange=${handleChange}
                disabled=${isReadOnly}
              />`)
          }
        </div>

        <div class="ms-Dialog-actions">
          <button type="button" class="ms-Button ms-Dialog-action" onClick=${onClose}>
            <span class="ms-Button-label">Cancelar</span>
          </button>
          ${!isReadOnly ? html`
            <button
              type="button"
              class="ms-Button ms-Dialog-action ms-Button--primary"
              onClick=${handleSave}
            >
              <span class="ms-Button-label">${isEdit ? 'Guardar cambios' : 'Agregar'}</span>
            </button>` : null}
        </div>
      </div>
    </div>`;
}

// ─── componente principal ────────────────────────────────────────────────────

export function ExpandableTableRenderer(props) {
  const {
    disabled = false,
    errors = [],
    field,
    value,
    readonly = false,
  } = props;

  const {
    description,
    id,
    label,
    validate = {},
    properties = {},
  } = field;

  const { required } = validate;
  const { formId } = useContext(FormContext);
  const uid = prefixId(id, formId);

  // Filas actuales — soporta array, JSON string de array/objeto y múltiples objetos sin corchetes
  const rows = parseRowValue(value);

  // Columnas configuradas desde el diseñador
  const columns = parseColumns(field);
  const visibleColumns = columns.filter(c => c.visibleInTable !== false && c.visibleInTable !== 'false');
  const allColumns = columns;

  // Columna checkbox (máximo 1)
  const checkboxCol = columns.find(c => c.type === 'checkbox');

  // Permisos configurados en el diseñador (true por defecto)
  const canAdd      = field.allowAdd      !== false;
  const canEdit     = field.allowEdit     !== false;
  const canCheckbox = field.allowCheckbox !== false;

  // Estado del modal: 'add' | 'edit' | 'view' | null
  const [modalMode, setModalMode] = useState(null);
  const [editIndex, setEditIndex] = useState(null);
  const modalOpen = modalMode !== null;

  // Emitir cambio hacia el formulario.
  // NOTA: countKey y countCheckedKey NO se emiten aquí porque form-js identifica
  // los campos por field.id (no por field.key), por lo que cualquier onChange con
  // un field clonado ({...field, key: otroKey}) sobreescribiría el valor de la
  // tabla con el conteo. Los conteos se inyectan en render-form al hacer submit.
  const emitChange = (newRows) => {
    if (!props.onChange) return;
    props.onChange({ field, value: newRows });
  };

  const openAdd = () => {
    setEditIndex(null);
    setModalMode('add');
  };

  const openEdit = (idx) => {
    setEditIndex(idx);
    setModalMode('edit');
  };

  const openView = (idx) => {
    setEditIndex(idx);
    setModalMode('view');
  };

  const closeModal = () => {
    setModalMode(null);
    setEditIndex(null);
  };

  const handleSave = (rowData) => {
    let newRows;
    if (editIndex !== null) {
      newRows = rows.map((r, i) => (i === editIndex ? { ...rowData } : r));
    } else {
      newRows = [...rows, { ...rowData }];
    }
    emitChange(newRows);
    closeModal();
  };

  const handleDelete = (idx) => {
    if (window.confirm('¿Desea eliminar este registro?')) {
      emitChange(rows.filter((_, i) => i !== idx));
    }
  };

  // Toggle directo de checkbox desde la celda de la tabla (respeta canCheckbox)
  const handleCheckboxToggle = (idx, colKey) => {
    if (!canCheckbox) return;
    const newRows = rows.map((r, i) =>
      i === idx ? { ...r, [colKey]: !r[colKey] } : r
    );
    emitChange(newRows);
  };

  // ── renderizado en modo editor (sin onChange disponible: read-only preview) ──
  const isEditorPreview = !props.onChange;

  const errorMessageId =
    errors.length === 0 ? undefined : `${uid}-error-message`;

  return html`
    <div class=${editorFormFieldClasses('expandableTable', { disabled, readonly })}>

      <${Label} id=${uid} label=${label} required=${required} />

      <!-- tabla -->
      <div style="overflow-x:auto; margin-top:8px;">
        <table
          id=${uid + '-table'}
          style="
            width:100%; border-collapse:collapse;
            font-size:13px; background:#fff;
            border:1px solid #e0e0e0; border-radius:4px;
          "
        >
          <thead>
            <tr style="background:#f5f5f5;">
              ${visibleColumns.length === 0
                ? html`<th style="padding:8px 12px; text-align:left; border-bottom:1px solid #e0e0e0; color:#888;">
                    (Sin columnas configuradas)
                  </th>`
                : visibleColumns.map(col => html`
                  <th style="padding:8px 12px; text-align:left; border-bottom:1px solid #e0e0e0; font-weight:600;">
                    ${col.label || col.key}
                  </th>`)
              }
              ${!isEditorPreview && !disabled && !readonly && (canAdd || canEdit)
                ? html`<th style="padding:8px 12px; border-bottom:1px solid #e0e0e0; text-align:center; width:90px;">Acciones</th>`
                : null}
            </tr>
          </thead>
          <tbody>
            ${rows.length === 0
              ? html`
                <tr>
                  <td
                    colspan=${visibleColumns.length + 1}
                    style="padding:16px 12px; text-align:center; color:#aaa; font-style:italic;"
                  >
                    Sin registros
                  </td>
                </tr>`
              : rows.map((row, idx) => html`
                <tr style="border-bottom:1px solid #f0f0f0;">
                  ${visibleColumns.map(col => col.type === 'checkbox'
                    ? html`
                      <td style="padding:8px 12px; text-align:center;">
                        <input type="checkbox"
                          checked=${!!row[col.key]}
                          disabled=${isEditorPreview || disabled || readonly || !canCheckbox}
                          onChange=${() => handleCheckboxToggle(idx, col.key)}
                          style="width:15px;height:15px;${canCheckbox && !isEditorPreview && !disabled && !readonly ? 'cursor:pointer;' : 'cursor:default;'}" />
                      </td>`
                    : col.type === 'select'
                    ? html`<td style="padding:8px 12px;">${getSelectOptionLabel(col, row[col.key])}</td>`
                    : html`<td style="padding:8px 12px;">${row[col.key] ?? ''}</td>`
                  )}
                  ${!isEditorPreview && !disabled && !readonly
                    ? html`
                      <td style="padding:6px 12px; text-align:center; white-space:nowrap;">
                        ${canEdit
                          ? html`<button type="button" title="Editar" onClick=${() => openEdit(idx)}
                              style="background:#0078d4;color:#fff;border:none;border-radius:3px;
                                     cursor:pointer;padding:3px 8px;font-size:12px;margin-right:4px;">
                              ✏</button>
                             <button type="button" title="Eliminar" onClick=${() => handleDelete(idx)}
                              style="background:#a4262c;color:#fff;border:none;border-radius:3px;
                                     cursor:pointer;padding:3px 8px;font-size:12px;">
                              ✕</button>`
                          : html`<button type="button" title="Ver" onClick=${() => openView(idx)}
                              style="background:#666;color:#fff;border:none;border-radius:3px;
                                     cursor:pointer;padding:3px 8px;font-size:12px;">
                              👁</button>`
                        }
                      </td>`
                    : null}
                </tr>`)
            }
          </tbody>
        </table>
      </div>

      <!-- botón agregar -->
      ${!isEditorPreview && !disabled && !readonly && canAdd
        ? html`
          <button
            type="button"
            onClick=${openAdd}
            style="
              margin-top:10px; padding:6px 16px;
              background:#0078d4; color:#fff;
              border:none; border-radius:3px;
              cursor:pointer; font-size:13px;
              display:inline-flex; align-items:center; gap:6px;
            "
          >
            <span style="font-size:16px;line-height:1;">+</span>
            Agregar registro
          </button>`
        : null}

      <!-- descripción y errores -->
      <${Description} id=${uid} description=${description} />
      <${Errors} id=${uid} errors=${errors} />

      <!-- modal -->
      ${modalOpen && allColumns.length > 0
        ? html`
          <${RowModal}
            columns=${allColumns}
            rowData=${editIndex !== null ? rows[editIndex] : null}
            onSave=${handleSave}
            onClose=${closeModal}
            isEdit=${modalMode === 'edit'}
            isReadOnly=${modalMode === 'view'}
          />`
        : null}
      ${modalOpen && allColumns.length === 0
        ? html`
          <div
            class="modal-diagrama-viewer"
            style="
              position:fixed; top:0; left:0; width:100%; height:100%;
              background:rgba(0,0,0,0.45); z-index:9999;
              display:flex; align-items:center; justify-content:center;
            "
          >
            <div
              class="ms-Dialog ms-Dialog--lgHeader diagrama-viewer"
              style="width:400px; max-width:95vw; height:auto; min-height:auto; display:flex; flex-direction:column;"
            >
              <div class="ms-Dialog-title">
                <span>Tabla Expandible</span>
              </div>
              <div class="ms-Dialog-content" style="padding:16px 20px; text-align:center;">
                <p style="margin:0 0 8px;">No hay columnas configuradas para este componente.</p>
                <p style="font-size:12px; color:#888; margin:0;">Configure las columnas en el diseñador de formularios.</p>
              </div>
              <div class="ms-Dialog-actions">
                <button type="button" class="ms-Button ms-Dialog-action ms-Button--primary" onClick=${closeModal}>
                  <span class="ms-Button-label">Cerrar</span>
                </button>
              </div>
            </div>
          </div>`
        : null}
    </div>`;
}

// Configuración para la paleta del editor
ExpandableTableRenderer.config = {
  ...Textfield.config,
  type,
  label: 'Tabla Expandible',
  iconUrl: '',
  propertiesPanelEntries: ['label', 'description', 'key'],
};
